import React from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ExternalLink, Smartphone, Monitor, Download, ShoppingCart, RefreshCw, Layers } from "lucide-react";

export default function EcomDetail() {
  const navigate = useNavigate();

  const appDownloadUrl = "https://googledriveconvert.hickter.com/?download=1c5fSAroYQqEfN822-_vsyl3SvjvTQwF-";
  const webAppUrl = "https://ecom.hickter.com";

  const features = [
    {
      title: "Real-Time Sync (App + Website)",
      desc: "Instant bi-directional synchronization ensuring every order, product catalog update, and inventory count across your website storefront, mobile app, and merchant admin panel stays identical with sub-second latency.",
      icon: <RefreshCw size={20} className="text-blue-600 dark:text-cyan-400" />
    },
    {
      title: "Fully Customizable Storefront & App",
      desc: "Tailored frontend branding, customized catalog taxonomy, localized payment workflows, dynamic banners, and promotional pricing matrices designed for your online business.",
      icon: <Layers size={20} className="text-emerald-600 dark:text-emerald-400" />
    },
    {
      title: "App + Website Unified Operations",
      desc: "Unified customer accounts, cross-device shopping carts, saved wishlists, order history, and seamless checkout sessions whether buyers order via mobile app or desktop web.",
      icon: <Smartphone size={20} className="text-indigo-600 dark:text-indigo-400" />
    },
    {
      title: "Orders & Realtime Inventory Engine",
      desc: "Automated stock deductions upon checkout, low-stock threshold triggers, automated tax & shipping calculations, UPI QR payments, and instant customer dispatch tracking.",
      icon: <ShoppingCart size={20} className="text-amber-600 dark:text-amber-400" />
    }
  ];

  const techStack = ["React", "Next.js", "Android Native", "Node.js", "TailwindCSS", "Real-Time WebSockets", "Pusher Engine", "REST API"];

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-[#030712] text-slate-900 dark:text-slate-100 font-sans pb-32 pt-6 px-4 sm:px-6 lg:px-8 transition-colors duration-300">
      <div className="max-w-5xl mx-auto space-y-8">
        
        {/* TOP BACK BUTTON */}
        <div>
          <button 
            onClick={() => navigate(-1)}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-700 dark:text-slate-300 hover:text-blue-600 dark:hover:text-cyan-400 font-bold text-xs transition-all shadow-sm cursor-pointer group"
          >
            <ArrowLeft size={16} className="group-hover:-translate-x-1 transition-transform" />
            <span>Back to Products</span>
          </button>
        </div>

        {/* HERO CARD */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 sm:p-10 shadow-xl space-y-8 relative overflow-hidden">
          <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-6">
            <div className="flex items-center gap-5">
              <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-2.5 shrink-0 shadow-md flex items-center justify-center">
                <img 
                  src="/assets/logo/ecom.png" 
                  alt="eCom By Hickter Logo" 
                  className="w-full h-full object-contain rounded-xl"
                  onError={(e: any) => {
                    e.target.onerror = null;
                    e.target.src = "https://ecom.hickter.com/brand/ecom-icon.svg";
                  }}
                />
              </div>
              <div className="space-y-1">
                <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-50 dark:bg-emerald-500/10 border border-emerald-200 dark:border-emerald-500/20 text-emerald-600 dark:text-emerald-400 text-xs font-bold">
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                  Live Platform & Mobile App
                </div>
                <h1 className="text-2xl sm:text-4xl font-extrabold text-slate-900 dark:text-white tracking-tight">
                  eCom By Hickter
                </h1>
                <p className="text-slate-600 dark:text-slate-400 text-xs sm:text-sm font-medium">
                  Real-time sync e-commerce platform, fully customized for any online business — storefront, app and admin always in sync.
                </p>
              </div>
            </div>

            {/* ACTION LINKS */}
            <div className="flex items-center gap-3 w-full md:w-auto">
              <a
                href={appDownloadUrl}
                target="_blank"
                rel="noreferrer"
                className="btn-primary-blue flex-1 md:flex-initial py-3 px-5 text-xs font-bold rounded-xl flex items-center justify-center gap-2 !text-white shadow-lg shadow-blue-500/20"
              >
                <Smartphone size={16} className="!text-white" />
                <span className="!text-white">Download App</span>
                <Download size={14} className="!text-white" />
              </a>

              <a
                href={webAppUrl}
                target="_blank"
                rel="noreferrer"
                className="flex-1 md:flex-initial py-3 px-5 text-xs font-extrabold rounded-xl bg-black dark:bg-black text-white !text-white border border-slate-800 flex items-center justify-center gap-2 shadow-md hover:bg-slate-900 transition-colors"
              >
                <Monitor size={16} className="!text-white" />
                <span className="!text-white font-extrabold">Visit Store</span>
                <ExternalLink size={13} className="!text-white" />
              </a>
            </div>
          </div>

          <p className="text-slate-700 dark:text-slate-300 text-sm sm:text-base leading-relaxed">
            eCom By Hickter is an omni-channel e-commerce ecosystem built from the ground up to unify your website storefront, mobile customer app, and administrative control center. Every customer order, catalog change, inventory decrement, and coupon promotion synchronizes instantly in real time with zero latency.
          </p>

          {/* QUICK STATS */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 pt-4 border-t border-slate-100 dark:border-slate-800">
            <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/50">
              <p className="text-xs text-slate-500 dark:text-slate-400 font-semibold">Synchronization</p>
              <p className="text-lg font-extrabold text-slate-900 dark:text-white">Real-Time Sync</p>
            </div>
            <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/50">
              <p className="text-xs text-slate-500 dark:text-slate-400 font-semibold">Architecture</p>
              <p className="text-lg font-extrabold text-slate-900 dark:text-white">App + Web + Admin</p>
            </div>
            <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/50">
              <p className="text-xs text-slate-500 dark:text-slate-400 font-semibold">Design & Features</p>
              <p className="text-lg font-extrabold text-slate-900 dark:text-white">Fully Customizable</p>
            </div>
            <div className="p-4 rounded-2xl bg-slate-50 dark:bg-slate-800/50">
              <p className="text-xs text-slate-500 dark:text-slate-400 font-semibold">Orders & Stock</p>
              <p className="text-lg font-extrabold text-slate-900 dark:text-white">Auto Reconciled</p>
            </div>
          </div>
        </div>

        {/* CORE CAPABILITIES GRID */}
        <div className="space-y-4">
          <h2 className="text-xl sm:text-2xl font-bold text-slate-900 dark:text-white">Core Capabilities & Modules</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {features.map((f, i) => (
              <div 
                key={i}
                className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 p-6 rounded-2xl shadow-sm hover:shadow-md transition-shadow space-y-3"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2.5 rounded-xl bg-slate-100 dark:bg-slate-800">
                    {f.icon}
                  </div>
                  <h3 className="font-bold text-slate-900 dark:text-white text-base">{f.title}</h3>
                </div>
                <p className="text-slate-600 dark:text-slate-400 text-xs sm:text-sm leading-relaxed pl-1">
                  {f.desc}
                </p>
              </div>
            ))}
          </div>
        </div>

        {/* TECH STACK BADGES */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl p-6 sm:p-8 space-y-4 shadow-sm">
          <h2 className="text-lg sm:text-xl font-bold text-slate-900 dark:text-white">Technology Stack & Realtime Engine</h2>
          <div className="flex flex-wrap gap-2">
            {techStack.map((tech, i) => (
              <span 
                key={i}
                className="px-3.5 py-1.5 rounded-xl bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700 text-slate-700 dark:text-slate-300 text-xs font-semibold"
              >
                {tech}
              </span>
            ))}
          </div>
        </div>

      </div>

      {/* FIXED BOTTOM DOWNLOAD & ACCESS BAR */}
      <div className="fixed bottom-0 left-0 right-0 z-[110] bg-white/95 dark:bg-[#030712]/95 backdrop-blur-xl border-t border-slate-200 dark:border-slate-800 p-3 sm:p-4 shadow-2xl">
        <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2.5 sm:gap-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 p-1.5 shrink-0 hidden sm:flex items-center justify-center">
              <img 
                src="/assets/logo/ecom.png" 
                alt="eCom Logo" 
                className="w-full h-full object-contain rounded-lg"
                onError={(e: any) => {
                  e.target.onerror = null;
                  e.target.src = "https://ecom.hickter.com/brand/ecom-icon.svg";
                }}
              />
            </div>
            <div className="text-left">
              <h4 className="text-xs sm:text-sm font-extrabold text-slate-900 dark:text-white truncate">eCom By Hickter</h4>
              <p className="text-[10px] text-slate-500 dark:text-slate-400 font-medium hidden sm:block">Storefront, App and Admin Always in Sync</p>
            </div>
          </div>

          <div className="flex items-center gap-2.5 w-full sm:w-auto shrink-0">
            <a
              href={appDownloadUrl}
              target="_blank"
              rel="noreferrer"
              className="flex-1 sm:flex-initial py-2.5 px-5 rounded-xl bg-gradient-to-r from-blue-600 via-indigo-600 to-blue-700 hover:from-blue-500 hover:to-indigo-600 text-white font-extrabold text-xs flex items-center justify-center gap-2 transition-all shadow-md shadow-blue-500/25 !text-white"
            >
              <Download size={15} className="!text-white" />
              <span className="!text-white font-black">Get App</span>
            </a>

            <a
              href={webAppUrl}
              target="_blank"
              rel="noreferrer"
              className="flex-1 sm:flex-initial py-2.5 px-4 rounded-xl bg-black dark:bg-black text-white !text-white border border-slate-800 text-xs font-extrabold flex items-center justify-center gap-2 shadow-sm hover:bg-slate-900 transition-colors"
            >
              <Monitor size={15} className="!text-white" />
              <span className="!text-white font-extrabold">Web Login</span>
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
